export type TutorialAudience =
  | "SUPER_ADMIN"
  | "ADMIN"
  | "DESENVOLVEDOR"
  | "MEMBRO"
  | "OBSERVADOR";

export type TutorialIcon =
  | "start"
  | "membros"
  | "categorias"
  | "apps"
  | "motor"
  | "receitas"
  | "saude"
  | "help";

export type TutorialStep = {
  title: string;
  description: string;
};

/**
 * Captura de tela ilustrativa (visualBudget do editorial-manifest). Arquivos
 * em /public/tutorial. Regra do manifesto: NUNCA dados reais — telas com
 * pessoas usam nomes/equipes inteiramente sintéticos.
 */
export type TutorialScreenshot = {
  src: string;
  alt: string;
  caption?: string;
  width: number;
  height: number;
  variant: "desktop" | "mobile";
};

export type TutorialTopic = {
  id: string;
  title: string;
  summary: string;
  where?: string;
  href?: string;
  audiences: readonly TutorialAudience[];
  steps: readonly TutorialStep[];
  result?: string;
  tips?: readonly string[];
  warning?: string;
  keywords?: readonly string[];
  screenshots?: readonly TutorialScreenshot[];
};

export type TutorialChapter = {
  id: string;
  kicker: string;
  title: string;
  description: string;
  icon: TutorialIcon;
  topics: readonly TutorialTopic[];
};

export type TutorialRoute = {
  path: string;
  page: string;
  purpose: string;
  audiences: readonly TutorialAudience[];
  href?: string;
  redirect?: boolean;
};

export const TUTORIAL_UPDATED_AT = "3 de agosto de 2026";

export const AUDIENCE_LABELS: Record<TutorialAudience, string> = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  DESENVOLVEDOR: "Desenvolvedor",
  MEMBRO: "Membro",
  OBSERVADOR: "Observador",
};

const EVERYONE = [
  "SUPER_ADMIN",
  "ADMIN",
  "DESENVOLVEDOR",
  "MEMBRO",
  "OBSERVADOR",
] as const satisfies readonly TutorialAudience[];

/** Quem passa em `isAdmin()` (src/lib/auth.ts) e abre o painel /admin da Key. */
const ADMINS = [
  "SUPER_ADMIN",
  "ADMIN",
] as const satisfies readonly TutorialAudience[];

/**
 * Administradores + Desenvolvedor. "Desenvolvedor" aqui é público-alvo do
 * texto (o filtro que o leitor escolhe), não papel global: desde 2026-07-30 o
 * que um dev alcança vem dos perfis Dev L1/L2/L3, não do MemberRole.
 */
const ADMINS_E_DEV = [
  "SUPER_ADMIN",
  "ADMIN",
  "DESENVOLVEDOR",
] as const satisfies readonly TutorialAudience[];

export const RECOMMENDED_FLOW = [
  {
    title: "Entre com a sua conta",
    description:
      "Google, e-mail e senha, código por e-mail ou WhatsApp — qualquer caminho cai na mesma sessão, válida por 7 dias em toda a Suite.",
  },
  {
    title: "Veja seus apps no portal",
    description:
      "A tela inicial mostra só o que você pode acessar — cada card já abre o app autenticado.",
  },
  {
    title: "Cadastre pessoas e categorias",
    description:
      "Admin → Membros para cada pessoa, ou Categorias para liberar um grupo inteiro de uma vez.",
  },
  {
    title: "Declare apps e recursos",
    description:
      "Cada sistema da Suite e as áreas internas dele (recursos) nascem em Apps & Recursos — a base de toda matriz.",
  },
  {
    title: "Refine com a matriz e acompanhe",
    description:
      "Ajuste ação por recurso quando o papel padrão não bastar, e cheque a Saúde da Suite quando algo não abrir.",
  },
] as const;

export const TUTORIAL_CHAPTERS: readonly TutorialChapter[] = [
  {
    id: "primeiros-passos",
    kicker: "Capítulo 1",
    title: "Primeiros passos",
    description:
      "O que é a Maranata Key, como entrar e o que você encontra no portal e na administração.",
    icon: "start",
    topics: [
      {
        id: "o-que-e-a-key",
        title: "O que é a Maranata Key",
        summary:
          "A central de identidade e permissões de toda a Suite Maranata: aqui se decide quem entra em cada aplicativo e o que cada pessoa pode fazer dentro dele — os apps obedecem ao que for definido aqui.",
        audiences: EVERYONE,
        steps: [
          {
            title: "Perfil global",
            description:
              "O papel da pessoa na Maranata — Super Admin, Desenvolvedor, Pastor/Diretoria, Admin Igreja, Líder PG, Secretaria, Financeiro, Kids, Membro ou Observador.",
          },
          {
            title: "Papel em cada app",
            description:
              "Admin (tudo), Usuário (ver + incluir + editar) ou Viewer (só ver) — o piso de permissões dentro de um app específico.",
          },
          {
            title: "Ajustes finos",
            description:
              "Categorias somam permissões em lote; overrides individuais concedem ou negam por recurso — negado vence tudo.",
          },
          {
            title: "Escopos especiais",
            description:
              "Camada extra própria de dois apps: por evento no Acampamento e por liderança de PG no Maranata App.",
          },
        ],
        result:
          "Você entende a lógica que decide, pra qualquer pessoa, o que aparece e o que funciona em cada app da Suite.",
        tips: [
          "Super Admin é o único papel que sempre enxerga todos os apps com todas as permissões — o dono da Suite nunca fica trancado fora.",
          "Desenvolvedor não tem esse atalho: o alcance dele vem do perfil Dev L1 (ver tudo), Dev L2 (ver, incluir e editar) ou Dev L3 (tudo dentro dos apps).",
          "Observador é sempre somente-leitura, em tudo — em qualquer app, não só na Key.",
        ],
        keywords: ["visao geral", "camadas", "papel", "categoria", "override", "escopo"],
      },
      {
        id: "entrar-no-app",
        title: "Entrar no aplicativo",
        summary:
          "Quatro caminhos caem na mesma sessão, válida por 7 dias em toda a Suite (.maranata.app) — logou uma vez, entrou em todos os apps que você tem acesso, sem pedir login de novo.",
        where: "key.maranata.app/login",
        href: "/login",
        audiences: EVERYONE,
        steps: [
          {
            title: "Google",
            description:
              "Um clique, com o e-mail cadastrado como membro. Só entra quem já foi cadastrado em Membros e está ativo.",
          },
          {
            title: "E-mail e senha",
            description: "Para quem teve senha definida (ex.: conta da Produção).",
          },
          {
            title: "Código por e-mail",
            description:
              "Digite o e-mail → chega um código de 6 dígitos → digite o código. Vale 10 minutos.",
          },
          {
            title: "Código por WhatsApp",
            description:
              "Digite o telefone cadastrado → chega um código de 6 dígitos no WhatsApp (cai no seu e-mail se o WhatsApp não responder) → digite o código. Telefone compartilhado por mais de um cadastro pede também a data de nascimento pra desempatar.",
          },
        ],
        result: "Sessão criada — os outros apps da Suite reconhecem você automaticamente.",
        tips: [
          "Existe ainda o modo Observador: um link mágico que dá acesso somente-leitura a alguém de fora, sem criar conta — entrada própria em /observador/entrar, não por este formulário.",
        ],
        keywords: ["login", "google", "senha", "otp", "codigo", "whatsapp", "observador", "sessao"],
      },
      {
        id: "portal-de-apps",
        title: "Portal de apps",
        summary:
          "A primeira tela depois do login mostra somente os aplicativos que você pode acessar — cada card abre o app já autenticado.",
        where: "Tela inicial após login (/)",
        href: "/",
        audiences: EVERYONE,
        steps: [
          {
            title: "Veja só o que é seu",
            description:
              "Administradores (Super Admin/Admin) veem o catálogo inteiro pra gerenciar ou testar qualquer app; qualquer outra pessoa vê o acesso efetivo — direto ou herdado por categoria, com o papel mais forte entre os dois.",
          },
          {
            title: "Entenda a etiqueta \"via grupo\"",
            description:
              "Aparece no card quando o acesso veio de uma categoria, e não de uma concessão individual.",
          },
          {
            title: "Sem nenhum app liberado?",
            description:
              "\"Nenhum app liberado para você ainda\" — peça pra um Super Admin/Admin conceder acesso em Membros.",
          },
        ],
        result: "Um clique abre qualquer app que a pessoa pode usar, sem passar login de novo.",
        keywords: ["portal", "apps", "via grupo", "acesso efetivo"],
        screenshots: [
          {
            src: "/tutorial/key-portal-desktop.png",
            alt: "Portal da Key no computador: saudação a um membro fictício e a grade \"Meus apps\" com os cartões de cada app liberado",
            caption: "O portal de apps da Key no computador — pessoa e apps ilustrativos.",
            width: 1280,
            height: 800,
            variant: "desktop",
          },
          {
            src: "/tutorial/key-portal-mobile.png",
            alt: "Portal da Key no celular: a mesma grade de apps liberados, em coluna única",
            caption: "O mesmo portal no celular — pessoa e apps ilustrativos.",
            width: 390,
            height: 844,
            variant: "mobile",
          },
        ],
      },
      {
        id: "painel-de-administracao",
        title: "Painel de administração",
        summary:
          "O painel central reúne quatro portas — Membros, Categorias, Apps & Recursos e Saúde da Suite — com contadores ao vivo. Tudo que este manual descreve começa aqui.",
        where: "Portal → seção Admin, restrito a Super Admin/Admin (/admin)",
        href: "/admin",
        audiences: ADMINS,
        steps: [
          {
            title: "Veja o resumo",
            description:
              "O topo mostra quantos membros, categorias, apps e recursos estão ativos agora.",
          },
          {
            title: "Escolha a porta",
            description:
              "Cada módulo abre a respectiva tela de gestão — os próximos capítulos detalham cada uma.",
          },
        ],
        result: "Acesso rápido a qualquer área de gestão, com o tamanho atual da Suite à vista.",
        warning:
          "Nenhum nível de desenvolvedor abre este painel — nem o Dev L3, que é ADMIN em todos os OUTROS apps da Suite. Gerenciar membros, categorias e apps é só de Super Admin e Admin.",
        keywords: ["admin", "painel", "membros", "categorias", "apps", "saude", "desenvolvedor"],
        screenshots: [
          {
            src: "/tutorial/key-admin-desktop.png",
            alt: "Painel de administração no computador: contadores da Suite no topo e os seis módulos — Membros, Categorias, Apps & Recursos, Saúde da Suite, Tutorial e Central de tutoriais",
            caption: "O painel de administração da Key no computador — números ilustrativos.",
            width: 1280,
            height: 800,
            variant: "desktop",
          },
          {
            src: "/tutorial/key-admin-mobile.png",
            alt: "Painel de administração no celular: os mesmos módulos empilhados em coluna única",
            caption: "O mesmo painel no celular — números ilustrativos.",
            width: 390,
            height: 844,
            variant: "mobile",
          },
        ],
      },
    ],
  },
  {
    id: "pessoas-e-acesso",
    kicker: "Capítulo 2",
    title: "Pessoas e acesso",
    description:
      "O cadastro de cada pessoa e a tela mais importante da Key: quem entra em cada app e o que pode fazer dentro dele.",
    icon: "membros",
    topics: [
      {
        id: "encontrar-membros",
        title: "Encontrar um membro",
        summary:
          "Busca multi-forma e filtros combináveis sobre a lista completa, paginada de 50 em 50.",
        where: "/admin/members",
        href: "/admin/members",
        audiences: ADMINS,
        steps: [
          {
            title: "Busque num campo só",
            description:
              "Nome, um pedaço do e-mail (ex.: @gmail) ou parte do telefone — o mesmo campo aceita os três.",
          },
          {
            title: "Combine filtros",
            description:
              "Papel, gênero e igreja se cruzam (ex.: \"secretarias da igreja X\"); \"Limpar\" volta pra lista completa.",
          },
          {
            title: "Pagine sem perder o filtro",
            description:
              "\"página 1 de N\", com Anterior/Próxima mantendo os filtros aplicados.",
          },
        ],
        result: "A pessoa certa em poucos cliques, mesmo com centenas de membros cadastrados.",
        keywords: ["buscar", "filtro", "papel", "genero", "igreja", "paginacao"],
        screenshots: [
          {
            src: "/tutorial/key-admin-members-desktop.png",
            alt: "Lista de membros no computador: busca por nome/e-mail/telefone, filtros de papel, gênero e igreja, e as primeiras linhas de uma lista paginada com nomes e e-mails fictícios",
            caption: "A busca de membros no computador — nomes e e-mails fictícios.",
            width: 1280,
            height: 800,
            variant: "desktop",
          },
          {
            src: "/tutorial/key-admin-members-mobile.png",
            alt: "Lista de membros no celular: o mesmo formulário de busca e filtros, com a lista empilhada abaixo",
            caption: "A mesma busca de membros no celular — nomes e e-mails fictícios.",
            width: 390,
            height: 844,
            variant: "mobile",
          },
        ],
      },
      {
        id: "cadastrar-e-editar-membros",
        title: "Cadastrar e editar membros",
        summary:
          "O cadastro cobre nome, e-mail (o login), telefone, papel global, gênero, ícone e cor.",
        where: "/admin/members",
        href: "/admin/members",
        audiences: ADMINS,
        steps: [
          {
            title: "Adicione um membro",
            description:
              "\"＋ Adicionar membro\" no topo → preencha nome, e-mail (obrigatório: é o login), telefone, papel, gênero, ícone e cor → \"Adicionar membro\".",
          },
          {
            title: "Edite quem já existe",
            description:
              "Clique na linha da pessoa → o cartão expande com o formulário de edição, o botão de excluir e o link \"Gerenciar acesso aos apps →\".",
          },
          {
            title: "Defina o papel global",
            description:
              "O select \"Papel\" grava a camada 1 da permissão (Super Admin, Desenvolvedor, Pastor/Diretoria…) — membro comum usa o papel Membro.",
          },
        ],
        result: "A pessoa existe no sistema e pode fazer login assim que tiver pelo menos um app liberado.",
        keywords: ["cadastrar", "editar", "membro", "papel global", "email"],
      },
      {
        id: "conceder-acesso-aos-apps",
        title: "Conceder acesso aos apps (a matriz)",
        summary:
          "A tela mais importante da Key: mostra os grupos empilhados da pessoa, o resultado já resolvido (permissão efetiva) e — se precisar — o ajuste fino individual em cada recurso.",
        where: "/admin/members/[id] → \"Gerenciar acesso aos apps →\"",
        audiences: ADMINS,
        steps: [
          {
            title: "Veja os grupos empilhados",
            description:
              "\"Perfis & grupos\": todo grupo/categoria do qual a pessoa participa, com o papel de cada um (ou \"herdar padrão\") e um jeito de adicionar a mais um grupo manual. Grupos com 🔒 são geridos por sync (Rodízio, Ministério Maranata, Escala) — ajuste só na origem.",
          },
          {
            title: "Confira o resultado em \"Permissão efetiva\"",
            description:
              "Por app, o papel final e de onde ele veio (acesso direto e/ou cada grupo) — e, abaixo, a mesma matriz que o app recebe de verdade, célula por célula: herdado do papel, +grupo, +override ou −negado.",
          },
          {
            title: "Abra o perfil de acesso",
            description:
              "Na linha da pessoa → \"Gerenciar acesso aos apps →\". Aparece a lista de todos os apps da Suite.",
          },
          {
            title: "Ligue o app",
            description:
              "O interruptor laranja liga/desliga o acesso àquele app. Desligado = o app some do portal da pessoa.",
          },
          {
            title: "Escolha o papel no app",
            description:
              "Ao lado do interruptor: Admin (tudo), Usuário (ver + incluir + editar) ou Viewer (só ver) — o \"piso\" das permissões.",
          },
          {
            title: "Customize para esta pessoa",
            description:
              "\"Customizar para esta pessoa\", visível abaixo de cada app: cada linha é um recurso (ex.: eventos, relatórios) e cada coluna uma ação — Ver · Incluir · Editar · Excluir · Gerenciar. Clique numa célula pra alternar entre herdado, permitido e negado.",
          },
        ],
        result:
          "O app aparece (ou some) do portal da pessoa, com o piso de ações certo em cada recurso — e dá pra ver exatamente de onde cada permissão veio.",
        tips: [
          "\"Herdado\" é o que o papel no app + as categorias já dão. \"Permitido\" concede a mais. \"Negado\" tira — e vence qualquer herança.",
          "\"Gerenciar\" implica todas as outras ações do recurso.",
          "\"Permissão efetiva\" é só leitura — pra mudar algo de verdade, use \"Perfis & grupos\", o toggle do app ou \"Customizar para esta pessoa\".",
        ],
        warning:
          "O acesso efetivo de uma pessoa é sempre a união do toggle direto (esta tela) com o que ela recebe de cada categoria — com o papel mais forte valendo.",
        keywords: [
          "acesso",
          "matriz",
          "permissao",
          "recurso",
          "herdado",
          "permitido",
          "negado",
          "toggle",
          "perfis e grupos",
          "permissao efetiva",
          "grupos empilhados",
        ],
      },
    ],
  },
  {
    id: "categorias",
    kicker: "Capítulo 3",
    title: "Categorias",
    description: "Pacotes de permissão em lote — configure uma vez, encaixe quantas pessoas quiser.",
    icon: "categorias",
    topics: [
      {
        id: "montar-uma-categoria",
        title: "Montar e usar uma categoria",
        summary:
          "Categoria é um pacote de permissões em lote (ex.: a categoria membro, ou \"Pastores Caxias\") — monta uma vez, encaixa quantas pessoas quiser. Um assistente em 5 passos cria a categoria já pronta, com apps e matriz inclusos.",
        where: "/admin/grupos",
        href: "/admin/grupos",
        audiences: ADMINS,
        steps: [
          {
            title: "Abra o assistente",
            description:
              "Em /admin/grupos → \"Novo grupo ou perfil\" abre um assistente em 5 passos: Identidade, Alcance, Apps e papéis, Ajuste fino (opcional) e Revisão — nenhuma tela nova, só passos dentro da mesma página.",
          },
          {
            title: "Identidade",
            description:
              "Nome, slug (gerado sozinho do nome, editável) e descrição. Super Admin também vê \"Perfil reutilizável\" (marca a categoria pra aparecer em \"Perfis\" e servir de base pra copiar). Dá pra \"Copiar apps e matriz de um perfil existente\" — escolhe um perfil ou outra categoria já pronta e os passos 3/4 já chegam preenchidos, só ajustar.",
          },
          {
            title: "Escolha o alcance",
            description:
              "Geral (sem restrição) · Igreja local (uma das 14 congregações) · Ministério (catálogo vivo do Ministério Maranata, agrupado pelos 12 pilares — dá pra restringir a uma igreja também, virando \"ministério local\") · Pilar (um dos 12 responsáveis de pilar). Cada categoria tem só um alcance por vez.",
          },
          {
            title: "Apps e papéis, em lote",
            description:
              "Passo 3: cada app ativo com um interruptor + papel (Admin/Usuário/Viewer). Atalhos no topo aplicam o mesmo papel em TODOS de uma vez — \"Todos como Viewer/Usuário/Admin\" e o destacado \"Full\" (todos os apps como Admin). Dá pra desmarcar uma exceção pontual depois de usar o atalho.",
          },
          {
            title: "Ajuste fino (opcional)",
            description:
              "Passo 4: por app marcado que já tenha recursos declarados, a mesma matriz recurso×ação do passo seguinte. Deixar em branco não trava nada — dá pra configurar depois, na página da categoria já criada.",
          },
          {
            title: "Revise e crie",
            description:
              "Passo 5 resume identidade, alcance, os apps escolhidos com papel e quantas concessões a matriz vai ter → \"Criar grupo\" leva direto pra página da categoria recém-criada.",
          },
          {
            title: "Adicione pessoas",
            description:
              "Na página da categoria (depois de criada) → \"Adicionar membro\" → busque por nome/e-mail → Adicionar. Cada pessoa pode ter um papel diferente do padrão (override na própria linha).",
          },
          {
            title: "Conceda apps em lote a qualquer momento",
            description:
              "O mesmo atalho do passo 3 do assistente também vive na página de uma categoria já criada: seção \"Apps do grupo\" → escolha o papel → \"Aplicar\" concede todos os apps ativos de uma vez (ou use \"Full\" direto) — funciona inclusive em categorias com roster gerido por sync (o cadeado ali é só do roster, apps/matriz seguem livres).",
          },
          {
            title: "Refine por recurso",
            description:
              "Em cada app, \"▸ permissões por recurso\": as células concedidas pela categoria somam ao que cada pessoa já tem.",
          },
          {
            title: "Edite depois de criada",
            description:
              "Na página da categoria, \"▸ Editar grupo\" muda nome, descrição, papel padrão e alcance a qualquer momento (o slug da URL não muda por aqui).",
          },
        ],
        result: "Uma categoria nasce pronta (identidade + alcance + apps + matriz opcional) num assistente só — e continua ganhando acesso em lote a qualquer momento depois de criada, sem repetir a configuração pessoa por pessoa.",
        tips: [
          "O acesso efetivo de alguém é a união do individual com todas as categorias dela, com o papel mais forte valendo.",
          "Categorias nunca negam, só somam — pra restringir alguém específico, use o \"negado\" na matriz individual (capítulo 2).",
          "O seletor de ministério é o catálogo vivo do Ministério Maranata — um ministério novo aparece aqui assim que aprovado por lá, sem precisar de nada no Key.",
          "O alcance de cada categoria (chip ao lado do papel padrão, na lista e na página da categoria) já viaja no login de todo app da Suite — cada app pode aplicar sua própria regra conforme o alcance.",
          "\"Full\" existe nos dois lugares (assistente e página da categoria) porque é a concessão mais forte possível — use com a mesma cautela de dar papel Admin em tudo pra alguém.",
        ],
        keywords: [
          "categoria",
          "grupo",
          "lote",
          "papel padrao",
          "apps do grupo",
          "ministerio maranata",
          "catalogo",
          "congregacao",
          "pilar",
          "alcance",
          "escopo",
          "geral",
          "igreja local",
          "ministerio local",
          "assistente",
          "wizard",
          "novo grupo ou perfil",
          "passos",
          "copiar perfil",
          "perfil reutilizavel",
          "ajuste fino",
          "revisao",
          "concessao em lote",
          "full",
          "admin em todos os apps",
        ],
      },
      {
        id: "cargos-e-titulos",
        title: "Ver todos os cargos e títulos do ecossistema",
        summary:
          "Cargos & Títulos junta num só lugar os eixos de cargo espalhados pela Suite — pastoral, liderança ministerial, serviço federado, papéis globais do Key e papéis por evento do Acampamento — sempre lendo das fontes vivas, nunca um catálogo copiado que fica desatualizado.",
        where: "/admin/cargos",
        href: "/admin/cargos",
        audiences: ADMINS,
        steps: [
          {
            title: "Pastoral",
            description:
              "Quantos pastores de cada tipo (titular/auxiliar/colaborador e qualquer título novo do Rodízio), função aditiva (sênior/presidente/administrativo) e regime (integral/parcial) — direto do Core, com o grupo canônico e os apps que ele concede.",
          },
          {
            title: "Liderança ministerial",
            description:
              "Postos ativos por escopo (local/geral/pilar), com o nome do ministério/pilar já resolvido pelo catálogo vivo do Ministério Maranata.",
          },
          {
            title: "Serviço federado",
            description:
              "Os 4 grupos de professor/coordenador de EBD, discipulador e mentor — contagem de membros e apps concedidos.",
          },
          {
            title: "Papéis globais e do Acampamento",
            description:
              "Quantas pessoas em cada papel global do Key (Super Admin, Admin Igreja...) e uma referência dos papéis do Acampamento (coordenador/líder/hóspede), geridos dentro do próprio app.",
          },
        ],
        result:
          "Uma visão única de \"quem tem qual cargo\" sem visitar vários lugares — e sem risco de ficar desatualizada, porque nada aqui é copiado: tudo é lido ao vivo das fontes.",
        tips: [
          "Um cargo pastoral sem grupo canônico ainda (ex.: um título novo criado no Rodízio) aparece com \"— sem grupo canônico no Key ainda\": o sync cria esse grupo automaticamente na próxima sincronização, sem precisar de deploy.",
        ],
        keywords: [
          "cargos",
          "titulos",
          "catalogo",
          "pastoral",
          "lideranca",
          "servico federado",
          "papeis globais",
          "acampamento",
          "coordenador",
        ],
      },
    ],
  },
  {
    id: "apps-e-recursos",
    kicker: "Capítulo 4",
    title: "Apps & Recursos",
    description: "O catálogo de sistemas da Suite e as áreas internas que alimentam toda matriz de permissão.",
    icon: "apps",
    topics: [
      {
        id: "gerenciar-apps-e-recursos",
        title: "Cadastrar apps e seus recursos",
        summary:
          "Cada app tem nome, ícone, cor, URL e o interruptor geral (desligado, o app some pra todo mundo). O mais importante dentro de cada um são os recursos — as áreas internas que aparecem nas matrizes de permissão.",
        where: "/admin/apps",
        href: "/admin/apps",
        audiences: ADMINS,
        steps: [
          {
            title: "Veja os recursos de um app",
            description:
              "\"recursos →\" na linha do app. Recursos são as áreas internas (ex.: no financeiro: envelopes, lançamentos, importações, fechamentos, obras…).",
          },
          {
            title: "Crie um recurso novo",
            description:
              "No fim da página: slug (ex.: relatorios), nome e descrição → \"Adicionar recurso\". Na hora ele passa a existir em todas as matrizes.",
          },
          {
            title: "Edite, desative ou remova",
            description:
              "Cada linha tem editar (nome, ordem), o interruptor (desativado sai das matrizes sem apagar nada) e remover (apaga também as permissões ligadas a ele).",
          },
        ],
        result: "A base de recursos pronta pra Membros e Categorias concederem permissão em cima dela.",
        tips: [
          "Os apps da Suite já vêm com dezenas de recursos mapeados diretamente do código real de cada um. Só crie recurso novo quando um app ganhar uma área nova de verdade.",
        ],
        keywords: ["apps", "recursos", "catalogo", "slug", "ativo"],
      },
    ],
  },
  {
    id: "como-a-permissao-e-decidida",
    kicker: "Capítulo 5",
    title: "Como a permissão é decidida",
    description: "A ordem de cálculo nos bastidores — e os dois apps com uma camada extra de escopo.",
    icon: "motor",
    topics: [
      {
        id: "ordem-de-calculo",
        title: "A ordem de cálculo",
        summary:
          "Quando alguém abre um app, a Key resolve as permissões em três camadas, nesta ordem — e entrega o resultado já pronto.",
        audiences: ADMINS_E_DEV,
        steps: [
          {
            title: "Piso do papel no app",
            description: "Admin = tudo · Usuário = ver + incluir + editar · Viewer = ver.",
          },
          {
            title: "+ categorias",
            description: "Somam as concessões de cada categoria da pessoa.",
          },
          {
            title: "+ permitidos e − negados individuais",
            description:
              "Overrides concedem a mais (permitido) ou tiram (negado) — negado vence tudo, mesmo o que veio de categoria.",
          },
        ],
        result:
          "O app final só esconde botões e bloqueia ações conforme essa matriz — ele não recalcula nada, só aplica o que a Key mandou.",
        tips: [
          "Super Admin pula as três camadas (sempre pode tudo); Observador é rebaixado pra somente-leitura sempre, não importa a matriz. Os perfis de dev (L1/L2/L3) passam pelas camadas como qualquer outro grupo — eles entram na camada 1, como piso de papel em cada app.",
        ],
        keywords: ["ordem", "calculo", "piso", "categoria", "override", "negado", "resolucao"],
      },
      {
        id: "escopos-especiais",
        title: "Escopos especiais: Acampamento e PGs",
        summary: "Dois apps têm uma camada própria de escopo, além da matriz da Key.",
        audiences: ADMINS,
        steps: [
          {
            title: "Acampamento, por evento",
            description:
              "Dentro de cada evento (aba Participantes → \"Permissões neste evento\"), a permissão é refinada por pessoa naquele evento — coordenador gerencia tudo, líder edita hóspedes/equipes da própria igreja, e overrides ajustam.",
          },
          {
            title: "Maranata App, por PG",
            description:
              "Líder de PG só gerencia o(s) Pequeno(s) Grupo(s) que lidera — não mexe no grupo de outro líder. Criar ou excluir PG continua sendo dos administradores.",
          },
        ],
        result:
          "A mesma pessoa pode ser coordenadora do Retiro de Jovens e simples inscrita no Acampamento de Casais, sem mudar nada no perfil global dela.",
        tips: ["É a permissão certa, no evento certo — sem precisar duplicar o cadastro da pessoa."],
        keywords: ["acampamento", "evento", "pg", "pequeno grupo", "escopo", "coordenador"],
      },
      {
        id: "auditar-quem-alcanca",
        title: "Auditar quem alcança cada recurso",
        summary:
          "A matriz invertida: em vez de perguntar o que uma pessoa pode, parta do recurso e veja todo mundo que chega nele — e por qual caminho.",
        where: "/admin/auditoria",
        href: "/admin/auditoria",
        audiences: ["SUPER_ADMIN"],
        steps: [
          {
            title: "Escolha o app",
            description:
              "As pílulas no topo trocam o app auditado; o número ao lado é quantas pessoas têm o app.",
          },
          {
            title: "Leia a linha do recurso",
            description:
              "Cada recurso mostra a contagem por ação (Ver/Incluir/Editar/Excluir/Gerenciar). Excluir e Gerenciar ganham destaque — são as ações que merecem revisão periódica.",
          },
          {
            title: "Abra e siga o caminho",
            description:
              "Expandindo o recurso, cada pessoa aparece com o porquê: piso do papel (direto ou via grupo), categoria que concedeu, permissão individual ou acesso total de Super Admin.",
          },
        ],
        result:
          "Revisão de acesso vira consulta: \"quem consegue excluir em financeiro?\" se responde em dois cliques, com a trilha completa.",
        tips: [
          "A visão cobre o banco da Key e também a federação do Brito Auth (os super admins da família aparecem com o caminho \"federação\"); se a consulta à federação falhar, o topo da tela avisa que a visão degradou pro banco local. Só as checagens de Super Admin embutidas nos apps seguem fora.",
          "Negações individuais aparecem em vermelho no fim do recurso — inclusive as sem efeito hoje, porque continuam cadastradas.",
          "Toda segunda o relatório semanal de acessos compara a matriz com a da semana anterior e lista quem ganhou ou perdeu alcance — sem mudança, a seção diz isso explicitamente.",
        ],
        keywords: ["auditoria", "access review", "matriz", "quem alcanca", "recurso", "caminho", "revisao"],
      },
    ],
  },
  {
    id: "receitas-praticas",
    kicker: "Capítulo 6",
    title: "Receitas práticas",
    description: "Respostas diretas pras perguntas mais comuns de quem administra a Suite.",
    icon: "receitas",
    topics: [
      {
        id: "receita-dar-acesso-a-um-app",
        title: "Dar acesso a um app pra alguém",
        summary: "O caminho mais curto pra uma pessoa ganhar um app novo no portal dela.",
        audiences: ADMINS,
        steps: [
          {
            title: "Abra o perfil de acesso",
            description: "Membros → busque a pessoa → \"Gerenciar acesso aos apps →\".",
          },
          {
            title: "Ligue e escolha o papel",
            description:
              "Ligue o interruptor do app e escolha o papel — Admin, Usuário ou Viewer. O app já aparece no portal dela.",
          },
        ],
        keywords: ["dar acesso", "liberar app", "conceder"],
      },
      {
        id: "receita-ver-sem-excluir",
        title: "Deixar alguém ver e editar, mas nunca excluir",
        summary: "Um piso que já cobre a maioria dos casos, com uma exceção pontual quando precisar.",
        audiences: ADMINS,
        steps: [
          {
            title: "Dê o papel Usuário",
            description: "Usuário já não exclui nada — ver + incluir + editar.",
          },
          {
            title: "Refine se precisar",
            description:
              "Pra tirar o editar de um recurso específico também: abra a matriz e clique na célula \"Editar\" daquele recurso até ficar negado.",
          },
        ],
        keywords: ["usuario", "sem excluir", "negar edicao"],
      },
      {
        id: "receita-liberar-um-grupo",
        title: "Liberar um grupo inteiro de uma vez",
        summary: "Pra não repetir a mesma configuração pessoa por pessoa (ex.: todas as secretárias).",
        audiences: ADMINS,
        steps: [
          {
            title: "Crie a categoria",
            description: "Em Categorias, dê os apps e as permissões à categoria.",
          },
          {
            title: "Adicione as pessoas",
            description:
              "Quem entrar na categoria depois já ganha tudo automaticamente, sem repetir a configuração.",
          },
        ],
        keywords: ["categoria", "grupo", "lote", "secretarias"],
      },
      {
        id: "receita-dev-sem-gerenciar-pessoas",
        title: "Dar acesso a um dev na medida certa, sem ele mexer em pessoas",
        summary: "São três níveis, e você escolhe até onde ele chega.",
        audiences: ADMINS,
        steps: [
          {
            title: "Escolha o nível",
            description:
              "No cadastro do membro, adicione um dos perfis: Dev L1 (vê todos os apps, não escreve em nada), Dev L2 (vê, inclui e edita em todos) ou Dev L3 (ADMIN em todos, inclusive excluir e gerenciar). Cada nível alcança mais que o anterior.",
          },
          {
            title: "Suba o nível quando fizer sentido",
            description:
              "Trocar o perfil do membro é a única coisa necessária — não há mudança de código. E dá pra empilhar: Dev L1 como piso mais um acesso direto mais forte só no app que a pessoa mantém.",
          },
          {
            title: "O que nenhum nível alcança",
            description:
              "Gerenciar membros, categorias, grupos e permissões da própria Key continua sendo só de Super Admin — nem o Dev L3 entra. Quem tem tudo é o super admin, e mais ninguém.",
          },
        ],
        keywords: ["desenvolvedor", "dev", "acesso tecnico", "nivel", "l1", "l2", "l3"],
      },
      {
        id: "receita-poder-so-num-evento",
        title: "Dar um poder só num evento do Acampamento",
        summary: "Sem tocar no perfil global da pessoa — o poder fica preso àquele evento.",
        audiences: ADMINS,
        steps: [
          {
            title: "Abra as permissões do evento",
            description:
              "No app Acampamento → evento → Participantes → ícone de escudo \"Permissões neste evento\" → matriz daquela pessoa, só naquele evento.",
          },
          {
            title: "Lembre do escopo",
            description:
              "Ela pode ser coordenadora ali e simples inscrita em qualquer outro evento — nada muda fora dele.",
          },
        ],
        keywords: ["acampamento", "evento", "coordenador", "escopo pontual"],
      },
      {
        id: "receita-acesso-sem-conta",
        title: "Dar uma olhada pra alguém de fora, sem criar conta",
        summary: "O modo Observador existe exatamente pra visitas pontuais e auditorias externas.",
        audiences: ADMINS,
        steps: [
          {
            title: "Use o modo Observador",
            description:
              "Um link mágico dá acesso somente-leitura — a pessoa entra e não consegue alterar nada, em nenhum app da Suite.",
          },
        ],
        keywords: ["observador", "link magico", "somente leitura", "visita"],
      },
    ],
  },
  {
    id: "saude-da-suite",
    kicker: "Capítulo 7",
    title: "Saúde da Suite",
    description: "O painel de status: o que está no ar e os números da Suite.",
    icon: "saude",
    topics: [
      {
        id: "acompanhar-a-saude",
        title: "Acompanhar a Saúde da Suite",
        summary:
          "Ping de cada app da Suite (no ar ou fora) e estatísticas de membros, categorias e sessões — o primeiro lugar pra olhar quando \"algum app não abre\".",
        where: "/saude",
        href: "/saude",
        audiences: ADMINS,
        steps: [
          {
            title: "Leia os quatro números",
            description:
              "Apps no ar, membros ativos, categorias e sessões vivas — atualizado a cada 60 segundos.",
          },
          {
            title: "Confira o status de cada app",
            description:
              "HEAD nas URLs cadastradas (timeout de 5s): 2xx–4xx conta como UP (o app está respondendo, mesmo que exija login), 5xx ou erro de rede é DOWN.",
          },
        ],
        result: "Diagnóstico rápido: app fora do ar aparece antes de qualquer chamado de suporte.",
        keywords: ["saude", "status", "ping", "up", "down", "sessoes"],
        screenshots: [
          {
            src: "/tutorial/key-saude-desktop.png",
            alt: "Saúde da Suite no computador: os quatro números do topo (apps no ar, membros ativos, grupos, sessões vivas) e a lista de status de cada app, todos UP",
            caption: "A Saúde da Suite no computador — números ilustrativos.",
            width: 1280,
            height: 800,
            variant: "desktop",
          },
          {
            src: "/tutorial/key-saude-mobile.png",
            alt: "Saúde da Suite no celular: os quatro números em grade 2×2 e a lista de status dos apps abaixo",
            caption: "A mesma tela no celular — números ilustrativos.",
            width: 390,
            height: 844,
            variant: "mobile",
          },
        ],
      },
    ],
  },
  {
    id: "ajuda",
    kicker: "Capítulo 8",
    title: "Ajuda",
    description: "Pra onde ir quando o assunto não é da Key.",
    icon: "help",
    topics: [
      {
        id: "onde-buscar-mais-ajuda",
        title: "Onde buscar mais ajuda",
        summary: "Este manual cobre a Key — identidade e permissões. Cada app da Suite tem o seu.",
        audiences: EVERYONE,
        steps: [
          {
            title: "Reabra este manual",
            description:
              "\"Tutorial — como usar a Maranata Key\", no rodapé do portal (ou em Admin → Tutorial), abre este guia de novo.",
          },
          {
            title: "Veja o mapa completo da Suite",
            description:
              "\"Central de tutoriais\", logo abaixo, abre tutorial.maranata.app — o hub com o manual de cada app do ecossistema.",
          },
          {
            title: "Direcione o assunto certo",
            description:
              "Cadastro de pessoas e igrejas → Maranata Core; escalas e rodízio → os apps de cada um; o que cada pessoa pode fazer em qualquer app → aqui, na Key.",
          },
        ],
        result: "Cada dúvida cai no manual certo, sem depender de decorar quem cuida de quê.",
        tips: [
          "Sem ser Super Admin/Admin, a maioria das áreas de administração mostra \"Acesso restrito a administradores\"; Categorias e Saúde da Suite te devolvem direto ao portal — os dois são sinais do mesmo motivo: falta o papel Super Admin ou Admin na sua conta.",
        ],
        keywords: ["ajuda", "central de tutoriais", "core", "acesso restrito", "administradores"],
      },
    ],
  },
];

export const TUTORIAL_ROUTES: readonly TutorialRoute[] = [
  {
    path: "/login",
    page: "Entrada",
    purpose: "Login por Google, e-mail/senha, código por e-mail ou por WhatsApp.",
    audiences: EVERYONE,
    href: "/login",
  },
  {
    path: "/observador/entrar",
    page: "Entrada do observador",
    purpose: "Login somente-leitura por link mágico, sem criar conta.",
    audiences: ["OBSERVADOR"],
  },
  {
    path: "/",
    page: "Portal",
    purpose: "Grid dos apps que a pessoa tem acesso efetivo (direto ou via categoria).",
    audiences: EVERYONE,
    href: "/",
  },
  {
    path: "/admin",
    page: "Administração",
    purpose:
      "Hub com contadores ao vivo — porta de entrada pra Membros, Categorias, Apps & Recursos e Saúde.",
    audiences: ADMINS,
    href: "/admin",
  },
  {
    path: "/admin/members",
    page: "Membros",
    purpose: "Busca, cadastro e edição de pessoas.",
    audiences: ADMINS,
    href: "/admin/members",
  },
  {
    path: "/admin/members/[id]",
    page: "Acesso do membro",
    purpose:
      "Perfis e grupos empilhados, permissão efetiva já resolvida (com a origem por célula) e a matriz de customização individual por recurso.",
    audiences: ADMINS,
  },
  {
    path: "/admin/grupos",
    page: "Categorias",
    purpose: "Listagem das categorias (pacotes de permissão em lote) e link pro assistente de criação.",
    audiences: ADMINS,
    href: "/admin/grupos",
  },
  {
    path: "/admin/grupos/novo",
    page: "Novo grupo ou perfil",
    purpose:
      "Assistente em 5 passos (identidade, alcance, apps e papéis, ajuste fino opcional, revisão) que cria a categoria já com apps/matriz — inclusive copiando de um perfil existente.",
    audiences: ADMINS,
    href: "/admin/grupos/novo",
  },
  {
    path: "/admin/grupos/[id]",
    page: "Categoria",
    purpose:
      "Membros da categoria, apps do grupo (com concessão em lote/Full) e a matriz de permissões dela.",
    audiences: ADMINS,
  },
  {
    path: "/admin/cargos",
    page: "Cargos & Títulos",
    purpose:
      "Catálogo único de todos os cargos/títulos do ecossistema (pastoral, liderança, serviço federado, papéis globais e do Acampamento), lido das fontes vivas.",
    audiences: ADMINS,
    href: "/admin/cargos",
  },
  {
    path: "/admin/acessos",
    page: "Acessos",
    purpose:
      "Inventário por pessoa: acesso amplo, perfis de dev, ociosos (45+ dias sem entrar) e tentativas barradas da semana.",
    audiences: ADMINS,
    href: "/admin/acessos",
  },
  {
    path: "/admin/auditoria",
    page: "Auditoria por recurso",
    purpose:
      "Matriz invertida de access review: recurso → quem alcança cada ação e por qual caminho.",
    audiences: ["SUPER_ADMIN"],
    href: "/admin/auditoria",
  },
  {
    path: "/admin/apps",
    page: "Apps & Recursos",
    purpose: "Catálogo de sistemas da Suite.",
    audiences: ADMINS,
    href: "/admin/apps",
  },
  {
    path: "/admin/apps/[id]",
    page: "Recursos do app",
    purpose: "CRUD dos recursos (áreas internas) de um app.",
    audiences: ADMINS,
  },
  {
    path: "/saude",
    page: "Saúde da Suite",
    purpose: "Ping dos apps e estatísticas de membros, categorias e sessões.",
    audiences: ADMINS,
    href: "/saude",
  },
  {
    path: "/tutorial",
    page: "Tutorial",
    purpose: "Este manual — público, sem gate de login.",
    audiences: EVERYONE,
    href: "/tutorial",
  },
];
